import { normalizeRutgersBaseCourseId } from '@/app/lib/courseIdFormatter';

export const EXPLORE_MODES = ['courses', 'degrees', 'professors', 'classrooms', 'research'] as const;
export type ExploreMode = typeof EXPLORE_MODES[number];

export const EXPLORE_MODE_ITEMS: ReadonlyArray<{ id: ExploreMode; label: string }> = [
  { id: 'courses', label: 'Courses' },
  { id: 'degrees', label: 'Degrees' },
  { id: 'professors', label: 'Professors' },
  { id: 'classrooms', label: 'Classrooms' },
  { id: 'research', label: 'Research' },
];

const EXPLORE_SELECTION_KEYS: Record<ExploreMode, string> = {
  courses: 'course',
  degrees: 'degree',
  professors: 'professor',
  classrooms: 'room',
  research: 'opportunity',
};
export const COURSE_WORKSPACE_VIEWS = ['ai-signals', 'syllabus', 'sections', 'professors', 'sirs'] as const;
export type CourseWorkspaceView = typeof COURSE_WORKSPACE_VIEWS[number];

export const COURSE_WORKSPACE_STORAGE_KEY = 'scarlet-sync:explore:course-workspace:v1';

export function exploreModeFrom(value: string | null | undefined): ExploreMode {
  return EXPLORE_MODES.includes(value as ExploreMode) ? value as ExploreMode : 'courses';
}

export function exploreModeQueryChange(nextMode: ExploreMode): Record<string, string | null> {
  return Object.fromEntries([
    ['view', nextMode === 'courses' ? null : nextMode],
    ...EXPLORE_MODES.map((mode) => [EXPLORE_SELECTION_KEYS[mode], mode === nextMode ? '__preserve__' : null] as const),
  ]);
}

export function courseWorkspaceViewFrom(value: string | null | undefined): CourseWorkspaceView {
  return COURSE_WORKSPACE_VIEWS.includes(value as CourseWorkspaceView) ? value as CourseWorkspaceView : 'ai-signals';
}

export function restoreCourseWorkspaceView(storage: Pick<Storage, 'getItem'> | null | undefined): CourseWorkspaceView {
  try { return courseWorkspaceViewFrom(storage?.getItem(COURSE_WORKSPACE_STORAGE_KEY)); } catch { return 'ai-signals'; }
}

export function persistCourseWorkspaceView(storage: Pick<Storage, 'setItem'> | null | undefined, view: CourseWorkspaceView) {
  try { storage?.setItem(COURSE_WORKSPACE_STORAGE_KEY, view); } catch { /* device storage is optional */ }
}

export function normalizeExploreCourseId(value: string): string | null {
  // Explore's guide endpoint is base-course-only. A section index belongs to
  // the selected term data and must not widen this public URL contract.
  if (!/^(?:\d{8}|\d{2}:\d{3}:\d{3})$/.test(value.trim())) return null;
  const normalized = normalizeRutgersBaseCourseId(value);
  return normalized ? normalized.replace(/\D/g, '') : null;
}
